import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'sonner';
import {
  Bot,
  X,
  Send,
  Sparkles,
  MessageSquare,
  Loader2,
  Trash2,
  ChevronDown,
  BarChart3,
  HelpCircle,
  Calculator,
  TrendingUp,
  Minimize2,
  Maximize2
} from 'lucide-react';

// Typing animation component
const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-2">
    <div className="flex gap-1">
      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
    <span className="text-xs text-slate-500 ml-2">MANDO IA está pensando...</span>
  </div>
);

// Message bubble component
const MessageBubble = ({ message, isUser }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
    <div
      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
        isUser
          ? 'bg-slate-900 text-white rounded-br-md'
          : 'bg-slate-100 text-slate-800 rounded-bl-md'
      }`}
    >
      {!isUser && (
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
          <div className="w-5 h-5 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center">
            <Bot className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs font-medium text-slate-500">MANDO IA</span>
        </div>
      )}
      <div className={`text-sm whitespace-pre-wrap ${isUser ? '' : 'prose prose-sm prose-slate max-w-none'}`}>
        {message.content}
      </div>
      <div className={`text-xs mt-2 ${isUser ? 'text-slate-400' : 'text-slate-400'}`}>
        {new Date(message.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  </div>
);

// Quick action buttons
const QuickActions = ({ onSelect, disabled }) => {
  const actions = [
    { icon: BarChart3, text: '¿Cómo va mi negocio?', query: '¿Cómo va mi negocio este mes? Dame un resumen de los indicadores clave.' },
    { icon: TrendingUp, text: 'Análisis de cobros', query: '¿Cuánto dinero tengo pendiente de cobrar? ¿Hay facturas vencidas?' },
    { icon: Calculator, text: 'Rentabilidad', query: '¿Cómo puedo calcular si una obra me está dando beneficio?' },
    { icon: HelpCircle, text: 'Ayuda con MANDO', query: '¿Qué puedo hacer con MANDO? Explícame las funciones principales.' },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-200 bg-slate-50">
      {actions.map((action, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(action.query)}
          disabled={disabled}
          className="flex items-center gap-2 p-2 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <action.icon className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate">{action.text}</span>
        </button>
      ))}
    </div>
  );
};

const MandoIA = () => {
  const { user, capabilities } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check MANDO IA status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await api.get('/mando-ia/status');
        setIsEnabled(response.data.enabled);
        setStatusReason(response.data.reason);
      } catch (error) {
        console.error('Error checking MANDO IA status:', error);
        setIsEnabled(false);
      }
    };

    if (user?.tenant_id) {
      checkStatus();
    }
  }, [user]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Send message to MANDO IA
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isTyping) return;

    const userMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);

    try {
      const response = await api.post('/mando-ia/chat', {
        message: text.trim(),
        session_id: sessionId,
        include_context: true
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('MANDO IA Error:', error);
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo.';
      
      if (errorDetail?.code === 'MANDO_IA_DISABLED') {
        errorMessage = errorDetail.message;
        setIsEnabled(false);
      } else if (errorDetail?.code === 'TRIAL_EXPIRED') {
        errorMessage = errorDetail.message;
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }

      const errorMsg = {
        role: 'assistant',
        content: `⚠️ ${errorMessage}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  }, [sessionId, isTyping]);

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(message);
  };

  // Clear conversation
  const clearConversation = async () => {
    if (sessionId) {
      try {
        await api.delete(`/mando-ia/session/${sessionId}`);
      } catch (error) {
        console.error('Error clearing session:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
    toast.success('Conversación eliminada');
  };

  // Don't render if user has no tenant
  if (!user?.tenant_id) return null;

  // Render disabled state message
  const renderDisabledMessage = () => {
    switch (statusReason) {
      case 'trial_expired':
        return 'Tu periodo de prueba ha expirado. Activa tu cuenta para usar MANDO IA.';
      case 'not_activated':
        return 'MANDO IA es una función premium. Contacta con tu administrador para activarla.';
      default:
        return 'MANDO IA no está disponible en este momento.';
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          data-testid="mando-ia-trigger"
        >
          <div className="relative">
            {/* Pulse effect */}
            {isEnabled && (
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-ping opacity-20" />
            )}
            
            {/* Main button */}
            <div className={`relative flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300 ${
              isEnabled 
                ? 'bg-gradient-to-r from-slate-900 to-slate-800 text-white hover:shadow-xl hover:scale-105'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isEnabled ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-slate-400'
              }`}>
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-sm">MANDO IA</span>
              {isEnabled && (
                <Sparkles className="w-4 h-4 text-yellow-400" />
              )}
            </div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-300 ease-out ${
            isMinimized
              ? 'bottom-6 right-6 w-72'
              : 'bottom-6 right-6 w-96 sm:w-[420px]'
          }`}
          data-testid="mando-ia-chat"
        >
          <Card className="overflow-hidden shadow-2xl border-slate-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      MANDO IA
                      {isEnabled && <Sparkles className="w-3.5 h-3.5 text-yellow-400" />}
                    </h3>
                    <p className="text-xs text-slate-400">
                      {isEnabled ? 'Asistente Inteligente' : 'No disponible'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={clearConversation}
                      className="text-slate-400 hover:text-white hover:bg-slate-700 w-8 h-8"
                      title="Limpiar conversación"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="text-slate-400 hover:text-white hover:bg-slate-700 w-8 h-8"
                  >
                    {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsOpen(false)}
                    className="text-slate-400 hover:text-white hover:bg-slate-700 w-8 h-8"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Content */}
            {!isMinimized && (
              <>
                {isEnabled ? (
                  <>
                    {/* Messages Area */}
                    <div className="h-80 overflow-y-auto bg-white p-4">
                      {messages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center px-4">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-600/10 flex items-center justify-center mb-4">
                            <MessageSquare className="w-8 h-8 text-slate-400" />
                          </div>
                          <h4 className="font-medium text-slate-700 mb-2">¡Hola! Soy MANDO IA</h4>
                          <p className="text-sm text-slate-500 mb-4">
                            Tu asistente inteligente para gestionar tu negocio. Pregúntame lo que necesites.
                          </p>
                          <div className="flex flex-wrap justify-center gap-2">
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">📊 Análisis</span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">💰 Finanzas</span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">🏗️ Obras</span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">❓ Ayuda</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {messages.map((msg, idx) => (
                            <MessageBubble
                              key={idx}
                              message={msg}
                              isUser={msg.role === 'user'}
                            />
                          ))}
                          {isTyping && <TypingIndicator />}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Quick Actions */}
                    {messages.length === 0 && (
                      <QuickActions onSelect={sendMessage} disabled={isTyping} />
                    )}

                    {/* Input Area */}
                    <form onSubmit={handleSubmit} className="p-3 border-t border-slate-200 bg-white">
                      <div className="flex gap-2">
                        <Input
                          ref={inputRef}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Escribe tu pregunta..."
                          disabled={isTyping}
                          className="flex-1 text-sm"
                          data-testid="mando-ia-input"
                        />
                        <Button
                          type="submit"
                          disabled={!message.trim() || isTyping}
                          className="bg-slate-900 hover:bg-slate-800"
                          data-testid="mando-ia-send"
                        >
                          {isTyping ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-400 mt-2 text-center">
                        Powered by GPT-4o · Datos en tiempo real
                      </p>
                    </form>
                  </>
                ) : (
                  /* Disabled State */
                  <div className="h-64 flex flex-col items-center justify-center p-6 text-center bg-slate-50">
                    <div className="w-16 h-16 rounded-2xl bg-slate-200 flex items-center justify-center mb-4">
                      <Bot className="w-8 h-8 text-slate-400" />
                    </div>
                    <h4 className="font-medium text-slate-700 mb-2">MANDO IA no disponible</h4>
                    <p className="text-sm text-slate-500">
                      {renderDisabledMessage()}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Minimized state */}
            {isMinimized && (
              <div className="p-3 bg-slate-50 text-center">
                <p className="text-xs text-slate-500">
                  {messages.length > 0 ? `${messages.length} mensajes` : 'Sin mensajes'}
                </p>
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
};

export default MandoIA;
